import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, type Event } from '@patsnap/tahiti-core/common/event';
import type { AIDisclosureState, AIDisclosureResultState } from '../type/ai-disclosure.type';
import { AIDisclosureStep } from '../type/ai-disclosure.enum';
import { AIDisclosureModel } from '../model/ai-disclosure.model';
import { HistoryService } from '@/services/history/service/history.service';
import { AIDisclosureResultService } from './ai-disclosure-result.service';
import {
    GetTechMeansDTO,
    PutTechMeansDTO,
    GetDisclosureHistoryDetailDTO,
} from '@/services/ai-disclosure/service/ai-disclosure.repository.interface';
import { TASK_TYPE } from '@/constants';
import { CheckSensitiveWordService } from '@/services/common/check-sensitive-word';

export const AIDisclosureService = Symbol('AIDisclosureService');

export interface AIDisclosureService {
    getModelData: () => AIDisclosureState;
    readonly onStatesChange: Event<AIDisclosureState>;
    updateInput: (input: string) => void;
    goResult: () => void;
    startGenerate: () => Promise<boolean>;
    putTechMeans: (techMeans: string) => void;
    init: (taskId?: string) => void;
}

@injectable()
export class AIDisclosureServiceImpl implements AIDisclosureService {
    private _onStatesChangeEmitter = new Emitter<AIDisclosureState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    constructor(
        @inject(AIDisclosureModel)
        private _model: AIDisclosureModel,
        @inject(HistoryService)
        private _historyService: HistoryService,
        @inject(AIDisclosureResultService)
        private _disclosureResultService: AIDisclosureResultService,
        @inject(CheckSensitiveWordService)
        private _checkSensitiveWordService: CheckSensitiveWordService,
        @inject(GetTechMeansDTO)
        private _getTechMeansDTO: GetTechMeansDTO,
        @inject(PutTechMeansDTO)
        private _putTechMeansDTO: PutTechMeansDTO,
        @inject(GetDisclosureHistoryDetailDTO)
        private _getDisclosureHistoryDetailDTO: GetDisclosureHistoryDetailDTO,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getModelData());
    }

    public getModelData() {
        return this._model.modelData;
    }

    private _setModelData(value: Partial<AIDisclosureState>) {
        this._model.update(modelData => {
            return { ...modelData, ...value };
        });
    }

    private _getHistoryModelData() {
        return this._historyService.getModelData();
    }

    public async checkSensitiveWord(text: string): Promise<string> {
        if (!text) return Promise.resolve('');
        return await this._checkSensitiveWordService.checkSensitiveWord(text);
    }

    public goResult() {
        this._setModelData({
            currentStep: AIDisclosureStep.DISCLOSURE_RESULT,
        });
        this._disclosureResultService.generateResult();
    }

    public async startGenerate(): Promise<boolean> {
        const { input, currentStep } = this.getModelData();
        const { taskId } = this._getHistoryModelData().historyDetailTemp;

        const sensitiveWord = await this.checkSensitiveWord(input);
        if (sensitiveWord) return false;

        const goalSettings = await this._getTechMeansDTO.fetch({ taskId, currentStep, input });

        if (goalSettings.techMeans) {
            this._disclosureResultService.updateSetting(goalSettings);
            return true;
        }
        return false;
    }

    public async putTechMeans(techMeans: string) {
        const { currentStep } = this.getModelData();
        const { taskId } = this._getHistoryModelData().historyDetailTemp;

        const sensitiveWord = await this.checkSensitiveWord(techMeans);
        if (sensitiveWord) return;

        const goalSettings = await this._putTechMeansDTO.fetch({ taskId, currentStep, techMeans });

        if (goalSettings.techMeans) {
            this._disclosureResultService.updateSetting(goalSettings);
        }
    }

    public updateInput(input: string) {
        this._setModelData({ input });
    }

    private _initDomainModel(modelData?: Partial<AIDisclosureState & AIDisclosureResultState>) {
        const { input = '', currentStep = AIDisclosureStep.DISCLOSURE_INPUT, ...resultModelData } = modelData || {};

        this._setModelData({
            input,
            currentStep,
        });

        this._disclosureResultService.initModel(resultModelData);
    }

    public async init(taskId = '') {
        if (taskId) {
            const res = await this._getDisclosureHistoryDetailDTO.fetch(taskId);
            this._historyService.updateTempData(temp => {
                return {
                    ...temp,
                    title: res.historyTitle || '',
                    taskId,
                };
            });
            this._initDomainModel(res);
        } else {
            await this._historyService.createTaskId(TASK_TYPE.DISCLOSURE);
            this._initDomainModel();
        }
    }
}

